/* oxlint-disable react-doctor/rendering-hydration-mismatch-time */
import { usePostHog } from '@posthog/react';

import PartyName from 'components/party/PartyName';
import { RecordLink } from 'components/record-chip/RecordLink';
import LinkWithOrigin from 'components/util/LinkWithOrigin';
import { commentParam } from 'config.ts';
import { differenceInDays, format, parseISO } from 'date-fns';
import useAuth from 'hooks/useAuth';
import { useEffectiveDateFormat } from 'hooks/useEffectiveDateFormat';
import IntlMessageFormat from 'intl-messageformat';
import isEqual from 'lodash/isEqual';
import { buildDebtReminderFilterParams } from 'pages/invoices/util/invoiceUtil';
import { getLinks } from 'pages/matters/util.tsx';
import MatterIndicators from 'components/matters/MatterIndicators';
import React, { Fragment } from 'react';
import { useIntl } from 'react-intl';
import { dispatch } from 'store';
import { closeTemporaryDrawer } from 'store/reducers/temporaryDrawer';
import { AutomatedNotification, NotificationActionType } from 'types/notifications';
import type { NamedEntityMinDto } from 'types/other';
import { cn } from 'utils/cn';
import { withoutYear } from 'utils/dateFormat';
import { formatDateStringInput } from 'utils/formatDateStringInput';
import { joinNames } from 'utils/getTranslations';
import { useLastNameFirst } from 'utils/nameOrder';
import { partyNameString } from 'utils/partyNameUtil';
import { draftPageOf } from 'components/create-dialog/recordTypes';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { draftNotificationKind, draftNotificationValues } from './draftNotification';
import { prependArticle } from './util.ts';

const LINK_CLASS = 'text-link hover:underline';

/** A contact named in a notification, as a link to `to`: in the new design its chip, when `to` is its record. */
function PartyLink({ party, to, onClick }: { party: NamedEntityMinDto; to: string; onClick: () => void }) {
    // The name `PartyName` writes for it; this DTO carries no number, suffix or mark.
    const label = partyNameString(party, { lastNameFirst: useLastNameFirst() });
    return (
        <RecordLink withOrigin onClick={onClick} to={to} className={LINK_CLASS} target={'_blank'} label={label}>
            <PartyName party={party} variant="readonly-other" />
        </RecordLink>
    );
}

/// !!! Format needs to include every placeholder the backend templates use; a missing one throws and the
/// row falls back to the notification title. Draft notifications are worded here instead, from their meta.
const FormattedNotifcationText: React.FC<{ notification: AutomatedNotification }> = React.memo(
    ({ notification }) => {
        const intl = useIntl();
        const { user } = useAuth();
        const posthog = usePostHog();
        const isNewDesign = useIsNewDesign();
        const dateFormat = useEffectiveDateFormat();
        // A delegation's dates are near: the tenant's format without the year, as in 10.11–20.11.
        const shortDate = (value: string) => format(parseISO(value), withoutYear(dateFormat));

        const closeDrawer = () => {
            dispatch(closeTemporaryDrawer());
        };

        // A comment notification opens the invoice's comments on that entry.
        const commentId = notification.metaDataJson?.commentId;
        const invoiceSearch = commentId !== undefined ? `?${commentParam}=${commentId}` : undefined;

        const handleLinkClick = () => {
            posthog.capture('notification_clicked', {
                category: notification.category,
                action_type: notification.metaDataJson?.action,
            });
            closeDrawer();
        };

        const getPartyUrl = (partyId: number, action?: string) => {
            if (action === NotificationActionType.OPEN_PARTY_INVOICE_CR) {
                return `/contact/${partyId}/invoices/cr?${buildDebtReminderFilterParams()}`;
            }
            return `/contact/${partyId}`;
        };

        const textClassName = cn('text-fg transition-colors duration-150 [&_a]:transition-opacity [&_a]:duration-150', {
            'text-fg-secondary [&_a]:opacity-70': notification?.readAt,
        });

        const draft = notification?.metaDataJson?.draft;
        const draftKind = draftNotificationKind(notification);
        if (draft && draftKind) {
            const values = draftNotificationValues(notification, intl);
            return (
                <p className={textClassName}>
                    {intl.formatMessage(
                        { id: `notification.draft-${draftKind}-text` },
                        {
                            ...values,
                            draft: (
                                <LinkWithOrigin
                                    key="notification-link-draft"
                                    onClick={() => handleLinkClick()}
                                    to={draftPageOf(draft.type, draft.id)}
                                    className={LINK_CLASS}
                                    target={'_blank'}
                                >
                                    {values.draft}
                                </LinkWithOrigin>
                            ),
                        }
                    )}
                </p>
            );
        }

        try {
            // The daily summary: under its sentence, a line per changed delegation with a link to it.
            const summaryLines = notification?.metaDataJson?.summary?.map((line) => {
                const head = intl.formatMessage(
                    { id: 'delegation.notification.summary-head' },
                    {
                        own: String(line.delegator.id === user?.id),
                        delegator: line.delegator.name,
                        startDate: shortDate(line.startDate),
                        endDate: shortDate(line.endDate),
                    }
                );
                const link = (
                    <LinkWithOrigin
                        onClick={() => handleLinkClick()}
                        to={`/delegations/${line.delegation.id}`}
                        className={LINK_CLASS}
                        target={'_blank'}
                    >
                        {intl.formatMessage({
                            id: isNewDesign
                                ? 'delegation.notification.open-delegation'
                                : 'delegation.notification.link-label',
                        })}
                    </LinkWithOrigin>
                );
                return isNewDesign ? (
                    <li key={line.delegation.id}>
                        <span className="text-foreground font-medium">{head}</span> {line.changes} {link}
                    </li>
                ) : (
                    <span key={line.delegation.id} className="text-fg">
                        · {head} {line.changes} {link}
                    </span>
                );
            });

            const text = (
                <p className={textClassName}>
                    {new IntlMessageFormat(notification.content).format({
                        matter: notification?.metaDataJson?.matter ? (
                            <Fragment key="notification-link-matter">
                                <MatterIndicators
                                    isConfidential={notification.metaDataJson.matter.isConfidential}
                                    className="mr-1"
                                />
                                <RecordLink
                                    withOrigin
                                    onClick={() => handleLinkClick()}
                                    to={`/matter/${notification.metaDataJson.matter.id}`}
                                    className={LINK_CLASS}
                                    target={'_blank'}
                                    label={notification.metaDataJson.matter.fullName}
                                />
                            </Fragment>
                        ) : undefined,
                        matterText: notification?.metaDataJson?.matter
                            ? notification.metaDataJson.matter.fullName
                            : undefined,
                        party: notification?.metaDataJson?.party ? (
                            <PartyLink
                                key="notification-link-party"
                                party={notification.metaDataJson.party}
                                onClick={() => handleLinkClick()}
                                to={getPartyUrl(notification.metaDataJson.party.id, notification.metaDataJson.action)}
                            />
                        ) : undefined,
                        partyText: notification?.metaDataJson?.party?.name,
                        partyName: notification?.metaDataJson?.partyName,
                        checkTypeName: notification?.metaDataJson?.checkTypeName,
                        daysUntilExpiry: notification?.metaDataJson?.daysUntilExpiry,
                        contractSentAt: notification?.metaDataJson?.contractSentAt
                            ? formatDateStringInput(new Date(notification.metaDataJson.contractSentAt))
                            : undefined,
                        invoice: notification?.metaDataJson?.invoice ? (
                            <RecordLink
                                withOrigin
                                key="notification-link-invoice"
                                onClick={() => handleLinkClick()}
                                to={`/invoice/${notification.metaDataJson.invoice.id}${invoiceSearch ?? ''}`}
                                entityType="INVOICE"
                                id={notification.metaDataJson.invoice.id}
                                search={invoiceSearch}
                                className={LINK_CLASS}
                                target={'_blank'}
                                label={notification.metaDataJson.invoice.invoiceNumber}
                            />
                        ) : undefined,
                        contract: notification?.metaDataJson?.contract ? (
                            <RecordLink
                                withOrigin
                                key="notification-link-contract"
                                onClick={() => handleLinkClick()}
                                to={`/contract/${notification.metaDataJson.contract.id}`}
                                className={LINK_CLASS}
                                target={'_blank'}
                                label={notification.metaDataJson.contract.name}
                            />
                        ) : undefined,
                        invoiceStatusTypeCode: notification?.metaDataJson?.invoice
                            ? prependArticle(notification?.metaDataJson?.invoice.invoiceStatusTypeCode?.toLowerCase())
                            : undefined,
                        changedByEmployee: notification?.metaDataJson?.invoiceTransition ? (
                            <RecordLink
                                withOrigin
                                key="notification-link-employee"
                                onClick={() => handleLinkClick()}
                                to={`/employee/${notification?.metaDataJson?.invoiceTransition?.changedByEmployee?.id}`}
                                className={LINK_CLASS}
                                target={'_blank'}
                                label={notification?.metaDataJson?.invoiceTransition?.changedByEmployee?.name ?? ''}
                            />
                        ) : undefined,
                        invoiceTransitionActionType: notification?.metaDataJson?.invoiceTransition
                            ? notification?.metaDataJson?.invoiceTransition.invoiceTransitionActionType?.toLowerCase()
                            : undefined,
                        comment: notification?.metaDataJson?.invoiceTransition
                            ? notification?.metaDataJson?.invoiceTransition.comment
                            : undefined,
                        fixedFee: notification?.metaDataJson?.billing
                            ? notification?.metaDataJson?.billing.fixedFee
                            : undefined,
                        capLimit: notification?.metaDataJson?.billing?.capLimit
                            ? notification?.metaDataJson?.billing.capLimit
                            : undefined,
                        project: notification?.metaDataJson?.project ? (
                            <RecordLink
                                withOrigin
                                key="notification-link-project"
                                onClick={() => handleLinkClick()}
                                // Classic's route matches no page; the chip takes the project's own.
                                to={`/project/${notification.metaDataJson.project.id}`}
                                entityType="PROJECT"
                                id={notification.metaDataJson.project.id}
                                className={LINK_CLASS}
                                target={'_blank'}
                                label={notification.metaDataJson.project.name}
                            />
                        ) : undefined,
                        // The delegator, a coordinator's copy of the delegator's alert and anyone told about someone
                        // else's substitute deep-link straight to the delegation; a substitute must land on the
                        // Substituting cases tab where the request awaits their acceptance, not the default My
                        // delegations tab.
                        delegation: notification?.metaDataJson?.delegation ? (
                            <LinkWithOrigin
                                key="notification-link-delegation"
                                onClick={() => handleLinkClick()}
                                to={
                                    notification.metaDataJson.delegator?.id === user?.id ||
                                    notification.metaDataJson.forCoordinator ||
                                    (notification.metaDataJson.substitute &&
                                        notification.metaDataJson.substitute.id !== user?.id)
                                        ? `/delegations/${notification.metaDataJson.delegation.id}`
                                        : '/delegations?tab=substituting'
                                }
                                className={LINK_CLASS}
                                target={'_blank'}
                            >
                                {intl.formatMessage({ id: 'delegation.notification.link-label' })}
                            </LinkWithOrigin>
                        ) : undefined,
                        delegator: notification?.metaDataJson?.delegator?.name ?? undefined,
                        substitute: notification?.metaDataJson?.substitute?.name ?? undefined,
                        coordinator: notification?.metaDataJson?.coordinator?.name ?? undefined,
                        pendingSubstitutes: notification?.metaDataJson?.pendingSubstitutes ?? undefined,
                        sender: notification?.metaDataJson?.sender?.name ?? undefined,
                        substitutes: notification?.metaDataJson?.substitutes
                            ? joinNames(notification.metaDataJson.substitutes.map((employee) => employee.name))
                            : undefined,
                        caseCount: notification?.metaDataJson?.caseCount ?? undefined,
                        count: notification?.metaDataJson?.count ?? undefined,
                        workingDays: notification?.metaDataJson?.workingDays ?? undefined,
                        // the select of "no working day left": started already, or a weekend start
                        started: String(notification?.metaDataJson?.started ?? true),
                        startDate: notification?.metaDataJson?.startDate
                            ? shortDate(notification.metaDataJson.startDate)
                            : undefined,
                        endDate: notification?.metaDataJson?.endDate
                            ? shortDate(notification.metaDataJson.endDate)
                            : undefined,
                        role: notification?.metaDataJson?.role ?? undefined,
                        returnedOn: notification?.metaDataJson?.returnedOn
                            ? format(parseISO(notification.metaDataJson.returnedOn), dateFormat)
                            : undefined,
                        cases: notification?.metaDataJson?.cases
                            ? notification?.metaDataJson?.cases.map((matter, index, arr) => (
                                  <Fragment key={`notification-link-case-${matter.id}`}>
                                      <MatterIndicators isConfidential={matter.isConfidential} className="mr-1" />
                                      <RecordLink
                                          withOrigin
                                          onClick={() => handleLinkClick()}
                                          to={`/matter/${matter.id}`}
                                          className={LINK_CLASS}
                                          target={'_blank'}
                                          label={matter.name}
                                      />
                                      {index < arr.length - 1 && ',\u00A0'}
                                  </Fragment>
                              ))
                            : undefined,
                        caseSupervisor: notification?.metaDataJson?.billing ? (
                            <RecordLink
                                withOrigin
                                key="notification-link-employee"
                                onClick={() => handleLinkClick()}
                                to={`/employee/${notification?.metaDataJson?.billing?.supervisedByEmployee?.id}`}
                                className={LINK_CLASS}
                                target={'_blank'}
                                label={notification?.metaDataJson?.billing?.supervisedByEmployee?.name ?? ''}
                            />
                        ) : undefined,
                        caseResponsible: notification?.metaDataJson?.billing?.responsibleEmployee ? (
                            <RecordLink
                                withOrigin
                                key="notification-link-employee"
                                onClick={() => handleLinkClick()}
                                to={`/employee/${notification?.metaDataJson?.billing?.responsibleEmployee?.id}`}
                                className={LINK_CLASS}
                                target={'_blank'}
                                label={notification?.metaDataJson?.billing?.responsibleEmployee?.name ?? ''}
                            />
                        ) : (
                            'none'
                        ),
                        threshold: notification?.metaDataJson?.threshold
                            ? notification?.metaDataJson?.threshold
                            : undefined,
                        parties: notification?.metaDataJson?.parties
                            ? notification?.metaDataJson?.parties.map((party, index, arr) => (
                                  <Fragment key={`notification-link-party-${party.id}`}>
                                      <PartyLink
                                          key="notification-link-party"
                                          party={party}
                                          onClick={() => handleLinkClick()}
                                          to={`/contact/${party.id}`}
                                      />
                                      {index < arr.length - 1 && ',\u00A0'}
                                  </Fragment>
                              ))
                            : undefined,
                        currency: notification?.metaDataJson?.billing
                            ? notification?.metaDataJson?.billing.currency
                            : undefined,
                        correspondence: notification?.metaDataJson?.correspondence ? (
                            <RecordLink
                                withOrigin
                                key="notification-link-correspondence"
                                onClick={() => handleLinkClick()}
                                to={`/correspondence/${notification?.metaDataJson?.correspondence?.id}`}
                                className={LINK_CLASS}
                                label={`${notification.metaDataJson.correspondence.name} (${notification.metaDataJson.correspondence.number})`}
                            >
                                {notification?.metaDataJson?.correspondence?.name} (
                                {notification?.metaDataJson?.correspondence?.number})
                            </RecordLink>
                        ) : undefined,
                        task: notification?.metaDataJson?.task ? (
                            <RecordLink
                                withOrigin
                                key={`notification-link-task-${notification?.metaDataJson?.task?.id}`}
                                onClick={() => handleLinkClick()}
                                to={`/task/${notification.metaDataJson.task.id}`}
                                className={LINK_CLASS}
                                label={notification?.metaDataJson?.task.name}
                            />
                        ) : undefined,
                        employees: notification?.metaDataJson?.employees
                            ? getLinks('employee', notification?.metaDataJson?.employees, {
                                  linkColor: 'text.link',
                                  onClick: handleLinkClick,
                              })
                            : undefined,
                        date: notification?.metaDataJson?.date ? notification?.metaDataJson?.date : undefined,
                        days: notification?.metaDataJson?.days ? notification?.metaDataJson?.days : undefined,
                        openedDate: notification?.metaDataJson?.openedDate ?? undefined,
                        reasons: notification?.metaDataJson?.reasons ?? undefined,
                        amlExtensionDate: notification?.metaDataJson?.amlExtensionDate
                            ? formatDateStringInput(parseISO(notification.metaDataJson.amlExtensionDate))
                            : undefined,
                        // {XX} days until autoblock is computed client-side from amlExtensionDate;
                        // the backend stores only the date to avoid server-timezone drift
                        amlDays: notification?.metaDataJson?.amlExtensionDate
                            ? Math.max(
                                  0,
                                  differenceInDays(parseISO(notification.metaDataJson.amlExtensionDate), new Date())
                              )
                            : undefined,
                        remainingAmount: notification?.metaDataJson?.remainingAmount
                            ? notification?.metaDataJson?.remainingAmount
                            : undefined,
                        remainingAmountCurrency: notification?.metaDataJson?.remainingAmountCurrency
                            ? notification?.metaDataJson?.remainingAmountCurrency
                            : undefined,
                        totalAmount: notification?.metaDataJson?.totalAmount
                            ? notification?.metaDataJson?.totalAmount
                            : undefined,
                        totalAmountCurrency: notification?.metaDataJson?.totalAmountCurrency
                            ? notification?.metaDataJson?.totalAmountCurrency
                            : undefined,
                        currentPartners: notification?.metaDataJson?.currentPartners
                            ? getLinks('employee', notification?.metaDataJson?.currentPartners, {
                                  linkColor: 'text.link',
                                  onClick: handleLinkClick,
                              })
                            : undefined,
                        previousPartners: notification?.metaDataJson?.previousPartners
                            ? getLinks('employee', notification?.metaDataJson?.previousPartners, {
                                  linkColor: 'text.link',
                                  onClick: handleLinkClick,
                              })
                            : undefined,
                        currentManagers: notification?.metaDataJson?.currentManagers
                            ? getLinks('employee', notification?.metaDataJson?.currentManagers, {
                                  linkColor: 'text.link',
                                  onClick: handleLinkClick,
                              })
                            : undefined,
                        previousManagers: notification?.metaDataJson?.previousManagers
                            ? getLinks('employee', notification?.metaDataJson?.previousManagers, {
                                  linkColor: 'text.link',
                                  onClick: handleLinkClick,
                              })
                            : undefined,
                    })}
                </p>
            );
            if (!summaryLines) {
                return text;
            }
            return (
                <>
                    {text}
                    {isNewDesign ? (
                        <ul className="text-meta text-foreground-secondary m-0 mt-1 flex list-none flex-col gap-1 p-0 leading-[1.54]">
                            {summaryLines}
                        </ul>
                    ) : (
                        summaryLines
                    )}
                </>
            );
        } catch (e) {
            console.error(e);
            return <p className={textClassName}>{notification.title}</p>;
        }
    },
    (prevProps, currentProps) => isEqual(prevProps.notification, currentProps.notification)
);

export default FormattedNotifcationText;
